import i18n from '@/utils/i18n';
import {
  ValidationSupport,
  type ValidationRule,
} from '@/graphql/generatedTypes';

import type {
  AddressFormState,
  FieldErrors,
  OpsOrderDetail,
  OpsOrderValidationRules,
} from './types';

// Address validation against the carrier-specific rules colugo sends with the
// order (`validation_rules`). Shared by the open-system and the event order
// forms so both edit a delivery address the same way.

type AddressRuleKey =
  | 'name'
  | 'company'
  | 'streetname'
  | 'additional_info'
  | 'city'
  | 'zip'
  | 'phone_number'
  | 'email'
  | 'country';

const ADDRESS_RULE_MAP: Record<keyof AddressFormState, AddressRuleKey> = {
  name: 'name',
  company: 'company',
  streetname: 'streetname',
  additional_info: 'additional_info',
  city: 'city',
  zip: 'zip',
  phone_number: 'phone_number',
  email: 'email',
  country: 'country',
};

export const ADDRESS_FIELDS = Object.keys(ADDRESS_RULE_MAP) as Array<
  keyof AddressFormState
>;

/**
 * The rules for the carrier currently selected in the form. `validation_rules`
 * on the order follows its stored carrier; `carrier_validation_rules` carries
 * one set per selectable carrier so a switch validates against the right one
 * before the order is saved.
 */
export function rulesForCarrier(
  order: Pick<OpsOrderDetail, 'validation_rules' | 'carrier_validation_rules'>,
  carrier: string | null | undefined,
): OpsOrderValidationRules {
  return (
    order.carrier_validation_rules.find((entry) => entry.carrier === carrier)
      ?.validation_rules ?? order.validation_rules
  );
}

export function normalizeName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

export function isAddressFieldSupported(
  rules: OpsOrderValidationRules,
  field: keyof AddressFormState,
): boolean {
  return (
    rules[ADDRESS_RULE_MAP[field]].support !== ValidationSupport.Unsupported
  );
}

export function isAddressFieldRequired(
  rules: OpsOrderValidationRules,
  field: keyof AddressFormState,
): boolean {
  return rules[ADDRESS_RULE_MAP[field]].support === ValidationSupport.Required;
}

export function validateAddressField(
  value: string,
  rule: ValidationRule,
): string | undefined {
  if (rule.support === ValidationSupport.Unsupported) return undefined;

  if (rule.support === ValidationSupport.Required && !value.trim()) {
    return i18n.t('Common:validationRequired');
  }

  if (value && rule.min != null && value.length < rule.min) {
    return i18n.t('Common:validationMinLength', { min: rule.min });
  }

  if (value && rule.max != null && value.length > rule.max) {
    return i18n.t('Common:validationMaxLength', { max: rule.max });
  }

  if (value && rule.regex) {
    try {
      if (!new RegExp(rule.regex).test(value)) {
        return i18n.t('Common:validationInvalidFormat');
      }
    } catch {
      // malformed regex from server
    }
  }

  return undefined;
}

/** Errors keyed `address.<field>`, empty when the address passes. */
export function validateAddress(
  rules: OpsOrderValidationRules,
  address: AddressFormState,
): FieldErrors {
  const errors: FieldErrors = {};
  for (const field of ADDRESS_FIELDS) {
    const err = validateAddressField(
      address[field],
      rules[ADDRESS_RULE_MAP[field]],
    );
    if (err) errors[`address.${field}`] = err;
  }
  return errors;
}
